// Reporting objectionable content.
//
// App Store Review Guideline 1.2 asks a user-generated-content app for four
// things: a way to filter objectionable material, a way for users to REPORT
// offensive content, a way to block abusive users, and published contact
// information. This module is the third of those four.
//
// ⚠ Scope, deliberately: this is REPORT only. Block-a-user is not built, and
// pretending otherwise in an App Review answer would be worse than the gap.
// Filtering is group-admin delete (`canModerate`), and contact information is
// the public /contact page.
//
// As of 2026-09-09 a USER can be reported, not just their content. That is
// still not "block": reporting tells the operator, blocking would stop the two
// people seeing each other, and only the first exists.
//
// The report is not a moderation queue and does not try to be. It writes an
// `audit_log` row -- which is why it needs no new table, and therefore no DDL
// on a production database whose app user deliberately has none -- and mails
// the site admin. A durable record plus a human who finds out is the whole of
// what "a mechanism to report, and timely responses" asks for at this size.
/**
 * What can be reported.
 *
 * ⚠ `user` is the odd one out and the reason several things below are worded
 * carefully. The three content targets each resolve to a GROUP, and reporting
 * one requires being a member of that group -- anyone who can see the content
 * can report it. A PERSON belongs to no group, so a user report carries a NULL
 * `group_id` and is authorized by nothing more than being signed in, which
 * matches profiles being visible to any signed-in user.
 *
 * Added 2026-09-09. Before that, an abusive PERSON could only be reported
 * obliquely by reporting something they had posted -- and not at all if they
 * had posted nothing, which is exactly the case for someone harassing people
 * over chat and deleting as they went.
 */
export const REPORT_TARGETS = ['post', 'message', 'photo', 'user'];
/** True for targets that live in a group, and so need a membership check. */
export function reportTargetNeedsGroup(target) {
    return target !== 'user';
}
/**
 * Why it is being reported.
 *
 * Kept SHORT on purpose. A long taxonomy reads as a form to fill in and gets
 * abandoned; five options is a menu. `other` carries the note, so nothing is
 * unreportable for want of a category.
 */
export const REPORT_REASONS = ['spam', 'harassment', 'sexual', 'violence', 'other'];
export const REPORT_REASON_LABELS = {
    spam: 'Spam or a scam',
    harassment: 'Harassment or hate',
    sexual: 'Sexual or explicit',
    violence: 'Violence or self-harm',
    other: 'Something else',
};
/** Cap on the free-text note. Matches the column it lands in being TEXT. */
export const REPORT_NOTE_MAX = 1000;
export function isReportTarget(v) {
    return typeof v === 'string' && REPORT_TARGETS.includes(v);
}
export function isReportReason(v) {
    return typeof v === 'string' && REPORT_REASONS.includes(v);
}
/**
 * The `action` written to `audit_log`. One value, not one per target, so the
 * whole reporting history is `WHERE action = 'content.report'` -- the query a
 * reviewer's question ("show me what was reported") actually starts from.
 */
export const REPORT_AUDIT_ACTION = 'content.report';
//# sourceMappingURL=report.js.map